import React, { useCallback, useEffect, useState } from 'react';
import ModalTransition from '@atlaskit/modal-dialog/modal-transition';
import { fg } from '@atlassian/jira-feature-gating';
import type { RouteContext } from '@atlassian/react-resource-router';
import { JSErrorBoundary } from '@jira/platform__js-error-boundary/src/JSErrorBoundary.tsx';
import WorkflowViewDiagramModalApp from '@jira/project-settings__workflow-view-diagram-modal/src/WorkflowViewDiagramModalApp.tsx';
import { LegacyPage } from '@jira/router-routes__common/src/ui/legacy-page/main.tsx';
import AddExistingWorkflowPreview from './AddExistingWorkflowPreview.tsx';

type Props = RouteContext;

const LEGACY_WORKFLOW_DIAGRAM_LINKS_CLASS = 'workflow-diagram-view';

// This id is the main entry point for the project settings workflows schemes page that can be found in:
// `jira-domains/infrastructure/webapp/jira-webapp-resources/src/main/webapp/secure/admin/views/workflow/editworkflowscheme.jsp#lines-16`
const LEGACY_WORKFLOW_SCHEMES_EDITOR_ID = 'workflowscheme-editor';

const getLegacyWorkflowDiagramLinks = () => {
	const workflowDiagramLinksElem = globalThis.document?.getElementsByClassName(
		LEGACY_WORKFLOW_DIAGRAM_LINKS_CLASS,
	);

	return [...(workflowDiagramLinksElem ?? [])];
};

const getLegacyWorkflowsSchemeEditorElement = () => {
	return globalThis.document?.getElementById(LEGACY_WORKFLOW_SCHEMES_EDITOR_ID);
};

const getLegacyWorkflowsPanelBody = () => {
	const workflowsPanel = globalThis.document?.getElementsByClassName('workflows-list');

	const workflowsPanelArray = [...(workflowsPanel ?? [])];
	return workflowsPanelArray.length > 0 ? workflowsPanelArray[0] : undefined;
};

export const ProjectSettingsWorkflowsSchemesEdit = (props: Props): React.JSX.Element => {
	const [selectedWorkflow, setSelectedWorkflow] = useState<string | undefined>(undefined);

	const handleOnClose = useCallback(() => {
		setSelectedWorkflow(undefined);
	}, [setSelectedWorkflow]);

	useEffect(() => {
		if (!fg('nike_entrypoint_migration_project_workflow')) {
			let observer: MutationObserver | null = null;

			const handleClicksOnWorkflowDiagramLinks = () => {
				const workflowsTable = getLegacyWorkflowsPanelBody();
				if (!workflowsTable) {
					return;
				}

				// Register click handlers to all legacy workflow diagram links
				workflowsTable.addEventListener('click', (event: Event) => {
					// We only want to handle clicks on the legacy workflow diagram links.
					// by checking the closest ancestor element with the class 'project-config-workflow-designer-link'
					// since the `event.target`can be any element inside the link (span for example)
					// oxlint-disable-next-line typescript/consistent-type-assertions
					const diagramLinkElement = (event.target as Element).closest(
						`.${LEGACY_WORKFLOW_DIAGRAM_LINKS_CLASS}`,
					);

					if (diagramLinkElement) {
						event.preventDefault();
						// We are missing the data attribute with the workflow name in the link element,
						// so we need to find the closest ancestor row and get the workflow name from there
						const workflowName = diagramLinkElement
							.closest('tr')
							?.getAttribute('data-workflow-name');
						if (workflowName) {
							setSelectedWorkflow(workflowName);
						}
					}
				});
			};

			// If there are already legacy workflow diagram links, we register the click handler immediately.
			if (getLegacyWorkflowDiagramLinks().length > 0) {
				handleClicksOnWorkflowDiagramLinks();
			} else {
				// We only execute this once, when the `#workflowscheme-editor` is populated
				// This is because the workflows schemes page is rendered server-side and the links are
				// dynamically added to the DOM after the initial render.
				observer = new MutationObserver(() => {
					handleClicksOnWorkflowDiagramLinks();
					observer?.disconnect();
				});
				const workflowsSchemeEditorElement = getLegacyWorkflowsSchemeEditorElement();
				if (workflowsSchemeEditorElement) {
					observer.observe(workflowsSchemeEditorElement, {
						childList: true,
						subtree: true,
					});
				}
			}

			return () => {
				getLegacyWorkflowsPanelBody()?.removeEventListener(
					'click',
					handleClicksOnWorkflowDiagramLinks,
				);
				observer?.disconnect();
			};
		}

		let observer: MutationObserver | null = null;
		let registeredWorkflowsTable: Element | undefined;

		const handleWorkflowDiagramLinkClick = (event: Event) => {
			// We only want to handle clicks on the legacy workflow diagram links.
			// by checking the closest ancestor element with the class 'project-config-workflow-designer-link'
			// since the `event.target`can be any element inside the link (span for example)
			// oxlint-disable-next-line typescript/consistent-type-assertions
			const diagramLinkElement = (event.target as Element).closest(
				`.${LEGACY_WORKFLOW_DIAGRAM_LINKS_CLASS}`,
			);

			if (diagramLinkElement) {
				event.preventDefault();
				// We are missing the data attribute with the workflow name in the link element,
				// so we need to find the closest ancestor row and get the workflow name from there
				const workflowName = diagramLinkElement.closest('tr')?.getAttribute('data-workflow-name');
				if (workflowName) {
					setSelectedWorkflow(workflowName);
				}
			}
		};

		const registerWorkflowDiagramLinkClickHandler = () => {
			const workflowsTable = getLegacyWorkflowsPanelBody();
			if (!workflowsTable) {
				return false;
			}

			workflowsTable.addEventListener('click', handleWorkflowDiagramLinkClick);
			registeredWorkflowsTable = workflowsTable;
			return true;
		};

		if (!registerWorkflowDiagramLinkClickHandler()) {
			// The workflow schemes page is rendered server-side and the editor and links are
			// dynamically added to the DOM after the initial render. Keep observing until the
			// workflows table exists so an early mutation cannot permanently miss registration.
			observer = new MutationObserver(() => {
				if (registerWorkflowDiagramLinkClickHandler()) {
					observer?.disconnect();
				}
			});
			const observationTarget =
				getLegacyWorkflowsSchemeEditorElement() ?? globalThis.document?.body;
			if (observationTarget) {
				observer.observe(observationTarget, {
					childList: true,
					subtree: true,
				});
			}
		}

		return () => {
			registeredWorkflowsTable?.removeEventListener('click', handleWorkflowDiagramLinkClick);
			observer?.disconnect();
		};
	}, []);

	return (
		<JSErrorBoundary
			id="read-only-workflow-schemes-diagram-error-boundary"
			packageName="jiraRouterRoutesLegacyGlobalSettingsWorkflowsSchemesEditRouteEntries"
			teamName="helix"
			fallback="flag"
		>
			<LegacyPage {...props} />
			<AddExistingWorkflowPreview />
			<ModalTransition>
				{selectedWorkflow && (
					<WorkflowViewDiagramModalApp
						workflowIdentifier={{ workflowName: selectedWorkflow }}
						onClose={handleOnClose}
					/>
				)}
			</ModalTransition>
		</JSErrorBoundary>
	);
};
